import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Recipe, RecipeInput, RECIPE_SELECT } from '../types/recipe';

/**
 * Ricette dalla tabella recipes. La RLS fa il filtro: i visitatori ricevono
 * solo quelle pubblicate, un admin con 2FA anche le bozze.
 */
export function useRecipes() {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.from('recipes').select(RECIPE_SELECT).order('created_at', { ascending: false });
    if (error) {
      console.error('Error fetching recipes:', error);
      setError('Non è stato possibile caricare le ricette');
    } else {
      setError(null);
      setRecipes((data ?? []) as Recipe[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Solo admin con 2FA. Restituisce la ricetta salvata. */
  const saveRecipe = async (id: string | null, input: RecipeInput): Promise<Recipe> => {
    const row = { ...input, updated_at: new Date().toISOString() };
    const query = id ? supabase.from('recipes').update(row).eq('id', id) : supabase.from('recipes').insert(row);
    const { data, error } = await query.select(RECIPE_SELECT);
    if (error) {
      if (error.code === '23505') throw new Error("Esiste già una ricetta con questo indirizzo: cambialo");
      throw new Error(error.message);
    }
    // Con RLS un salvataggio non autorizzato non dà errore: non tocca nessuna riga.
    if (!data || data.length === 0) throw new Error('Non autorizzato a modificare le ricette');
    const saved = data[0] as Recipe;
    setRecipes((prev) => (id ? prev.map((r) => (r.id === id ? saved : r)) : [saved, ...prev]));
    return saved;
  };

  const deleteRecipe = async (id: string) => {
    const { data, error } = await supabase.from('recipes').delete().eq('id', id).select('id');
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) throw new Error('Non autorizzato a eliminare le ricette');
    setRecipes((prev) => prev.filter((r) => r.id !== id));
  };

  return { recipes, loading, error, refresh, saveRecipe, deleteRecipe };
}

/** Una ricetta pubblicata per indirizzo; `notFound` se non esiste o è in bozza. */
export function useRecipe(slug: string | undefined) {
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from('recipes')
      .select(RECIPE_SELECT)
      .eq('slug', slug)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('Error fetching recipe:', error);
        setRecipe((data as Recipe | null) ?? null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { recipe, loading, notFound: !loading && !recipe };
}
